/**
 * Wizard step-renderer registry (design §8) — the forward-compat contract P4/P5
 * extend without touching this feature: each `features/<name>/wizard-steps/*.step.tsx`
 * module exports `{ type, component }` and is picked up by this glob.
 *
 * FIRST module in glob (alphabetical path) order wins a duplicate-type conflict
 * — deterministic; conflicts log a `console.warn` in dev (§8, §25 Q5). An
 * unregistered type NEVER throws: the frame falls back to the unknown-step
 * placeholder (§16) — that is the only fallback in the system.
 */
import type { ComponentType } from 'react';

import type { AgentTemplateWizardStepType, WizardStepModule, WizardStepProps } from '../../types';

const stepModules = import.meta.glob<WizardStepModule>('../../../*/wizard-steps/*.step.tsx', {
  eager: true,
});

const components = new Map<AgentTemplateWizardStepType, ComponentType<WizardStepProps>>();

for (const [path, mod] of Object.entries(stepModules).sort(([a], [b]) => a.localeCompare(b))) {
  if (!mod || typeof mod.type !== 'string' || !mod.component) {
    continue;
  }
  if (components.has(mod.type)) {
    if (import.meta.env.DEV) {
      console.warn(
        `[agents] ignoring duplicate wizard-step renderer for type "${mod.type}": ${path}`,
      );
    }
    continue;
  }
  components.set(mod.type, mod.component);
}

export const stepRegistry = {
  has(type: string): boolean {
    return components.has(type as AgentTemplateWizardStepType);
  },
  get(type: string): ComponentType<WizardStepProps> | undefined {
    return components.get(type as AgentTemplateWizardStepType);
  },
};
